<template>
  <ion-page>
    <PageHeaderWithMenu title="Разделы приложения" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-card class="admin-card admin-card--glass">
        <ion-card-header>
          <div class="admin-card-title-row">
            <ion-card-title>Обновление приложения</ion-card-title>
            <span
              v-if="runtimeConfig"
              class="update-status-badge"
              :class="runtimeConfig.updatePromptEnabled ? 'update-status-badge--on' : 'update-status-badge--off'"
            >
              {{ runtimeConfig.updatePromptEnabled ? 'Баннер включён' : 'Баннер выключен' }}
            </span>
          </div>
        </ion-card-header>
        <ion-card-content>
          <p class="admin-card-subtitle">
            Когда сборки уже вышли во всех сторах (App Store, Google Play, AppGallery), включите флаг —
            пользователям со старой версией покажется предложение обновиться. Закрытие баннера запоминается
            до следующей «кампании» (повторный показ — кнопкой ниже).
          </p>
          <div v-if="runtimeConfig" class="update-prompt-panel">
            <div class="update-prompt-control-row">
              <label class="app-section-toggle update-prompt-toggle">
                <span>Показывать баннер обновления</span>
                <ion-toggle
                  class="update-prompt-ion-toggle"
                  :checked="runtimeConfig.updatePromptEnabled"
                  mode="ios"
                  :disabled="savingRuntime"
                  @ionChange="onUpdatePromptToggle($event)"
                />
              </label>
              <span class="update-campaign-chip" title="Увеличивается при «Показать снова всем»">
                Кампания · {{ runtimeConfig.updatePromptVersion }}
              </span>
            </div>

            <div class="update-prompt-message-wrap">
              <div class="update-prompt-message-label">Текст сообщения</div>
              <ion-textarea
                class="update-prompt-textarea"
                v-model="runtimeConfig.updatePromptMessage"
                :auto-grow="true"
                :rows="3"
                maxlength="500"
                placeholder="Текст предложения обновить приложение"
              />
              <div class="update-prompt-message-hint">
                {{ (runtimeConfig.updatePromptMessage || '').length }}/500
              </div>
            </div>

            <div class="update-prompt-actions">
              <ion-button
                size="small"
                fill="solid"
                shape="round"
                class="update-prompt-btn update-prompt-btn--save"
                :disabled="savingRuntime"
                @click="saveRuntimeConfig(false)"
              >
                <ion-icon slot="start" :icon="saveOutline"></ion-icon>
                Сохранить текст
              </ion-button>
              <ion-button
                size="small"
                fill="outline"
                shape="round"
                class="update-prompt-btn update-prompt-btn--reshow"
                :disabled="savingRuntime || !runtimeConfig.updatePromptEnabled"
                @click="saveRuntimeConfig(true)"
              >
                <ion-icon slot="start" :icon="refreshOutline"></ion-icon>
                Показать снова всем
              </ion-button>
            </div>
          </div>
          <p v-else class="admin-hint">Настройки обновления не загружены.</p>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card admin-card--glass">
        <ion-card-header>
          <ion-card-title>Доступ к разделам приложения</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <p class="admin-card-subtitle">
            Включите «Доступно всем» для публичных разделов или выберите роль, которая открывает закрытый раздел.
          </p>
          <div v-if="sections.length" class="app-sections-list">
            <div
              v-for="section in sections"
              :key="section.sectionKey"
              class="app-section-row">
              <div class="app-section-info">
                <div class="app-section-title-row">
                  <h3>{{ section.name }}</h3>
                  <span :class="['app-section-status', section.availableToAll ? 'app-section-status--public' : 'app-section-status--role']">
                    {{ section.availableToAll ? 'Доступно всем' : 'По роли' }}
                  </span>
                </div>
                <p class="app-section-key">{{ section.sectionKey }}</p>
                <p class="app-section-role">
                  {{ section.availableToAll ? 'Раздел виден всем пользователям' : roleLabel(section.roleName) }}
                </p>
              </div>

              <div class="app-section-controls">
                <label class="app-section-toggle">
                  <span>Доступно всем</span>
                  <ion-toggle v-model="section.availableToAll" mode="ios" @ionChange="saveSection(section)"></ion-toggle>
                </label>

                <ion-item lines="none" :class="['app-section-role-select', { 'app-section-role-select--disabled': section.availableToAll }]">
                  <ion-select
                    label="Роль доступа"
                    label-placement="stacked"
                    v-model="section.roleName"
                    interface="popover"
                    :interface-options="roleSelectPopoverOptions"
                    mode="ios"
                    placeholder="Выберите роль"
                    :disabled="section.availableToAll"
                    @ionChange="saveSection(section)"
                  >
                    <ion-select-option v-for="a in allAuthorities" :key="a.name" :value="a.name">{{ a.description || a.name }}</ion-select-option>
                  </ion-select>
                </ion-item>
              </div>
            </div>
          </div>
          <p v-else class="admin-hint">Разделы приложения не загружены.</p>
        </ion-card-content>
      </ion-card>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { Authority } from '@/model/authority.model';
import { IAppSection } from '@/model/app-section.model';
import { GlobalContext } from '@/model/globalcontext.model';
import { appSectionService } from '@/services/app-section.service';
import AdminService from '@/services/admin.service';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonIcon,
  IonItem,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonToggle,
  toastController,
} from '@ionic/vue';
import { refreshOutline, saveOutline } from 'ionicons/icons';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import appConfigService, { IAppRuntimeConfig } from '@/services/app-config.service';
import { defineComponent, inject } from 'vue';

export default defineComponent({
  name: 'AdminAppSections',
  components: {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonIcon,
    IonItem,
    IonPage,
    IonSelect,
    IonSelectOption,
    IonTextarea,
    IonToggle,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return { isMobile: globalContext.isMobile };
  },
  data() {
    return {
      adminService: new AdminService(),
      allAuthorities: [] as Authority[],
      sections: [] as IAppSection[],
      savingKey: '',
      runtimeConfig: null as IAppRuntimeConfig | null,
      savingRuntime: false,
      roleSelectPopoverOptions: {
        cssClass: 'admin-role-select-popover',
        side: 'top',
        alignment: 'center',
      },
      headerScrolled: false,
      saveOutline,
      refreshOutline,
    };
  },
  async mounted() {
    await Promise.all([this.loadAuthorities(), this.loadSections(), this.loadRuntimeConfig()]);
  },
  methods: {
    async loadRuntimeConfig() {
      try {
        this.runtimeConfig = await appConfigService.getAdminConfig();
      } catch (error) {
        console.error('Failed to load runtime config', error);
        await this.showToast('Не удалось загрузить настройки обновления', 'danger');
      }
    },
    async onUpdatePromptToggle(ev: CustomEvent) {
      if (!this.runtimeConfig) return;
      this.runtimeConfig.updatePromptEnabled = !!(ev.detail as { checked?: boolean }).checked;
      await this.saveRuntimeConfig(false);
    },
    async saveRuntimeConfig(bumpPromptVersion: boolean) {
      if (!this.runtimeConfig) return;
      this.savingRuntime = true;
      try {
        const saved = await appConfigService.saveAdminConfig({
          updatePromptEnabled: this.runtimeConfig.updatePromptEnabled,
          updatePromptMessage: this.runtimeConfig.updatePromptMessage,
          bumpPromptVersion,
        });
        if (saved) this.runtimeConfig = saved;
        await this.showToast(
          bumpPromptVersion ? 'Баннер снова покажется пользователям' : 'Настройки обновления сохранены',
          'success'
        );
      } catch (error) {
        console.error('Failed to save runtime config', error);
        await this.showToast('Не удалось сохранить настройки обновления', 'danger');
        await this.loadRuntimeConfig();
      } finally {
        this.savingRuntime = false;
      }
    },
    async loadAuthorities() {
      try {
        this.allAuthorities = await this.adminService.getAdminAuthorities();
      } catch (error) {
        console.error('Failed to load authorities', error);
        await this.showToast('Не удалось загрузить роли', 'danger');
      }
    },
    async loadSections() {
      try {
        this.sections = await this.adminService.getAppSections();
      } catch (error) {
        console.error('Failed to load app sections', error);
        await this.showToast('Не удалось загрузить разделы приложения', 'danger');
      }
    },
    async saveSection(section: IAppSection) {
      if (!section.id) {
        await this.showToast('У раздела нет id', 'danger');
        return;
      }
      if (!section.availableToAll && !section.roleName) {
        await this.showToast('Для закрытого раздела нужно выбрать роль', 'warning');
        return;
      }
      this.savingKey = section.sectionKey;
      try {
        const saved = await this.adminService.updateAppSection(section.id, {
          ...section,
          roleName: section.availableToAll ? null : section.roleName,
        });
        const idx = this.sections.findIndex(s => s.id === saved.id);
        if (idx >= 0) {
          this.sections.splice(idx, 1, saved);
        }
        await appSectionService.loadSections(true);
        await this.showToast('Раздел сохранён', 'success');
      } catch (error) {
        console.error('Failed to save app section', error);
        await this.showToast('Не удалось сохранить раздел', 'danger');
      } finally {
        this.savingKey = '';
      }
    },
    roleLabel(roleName?: string | null): string {
      if (!roleName) {
        return 'Роль не выбрана';
      }
      const authority = this.allAuthorities.find(a => a.name === roleName);
      return authority?.description || roleName;
    },
    onContentScroll(event: CustomEvent) {
      const scrollTop = (event.detail as { scrollTop?: number }).scrollTop || 0;
      this.headerScrolled = scrollTop > 4;
    },
    async showToast(message: string, color: string) {
      const toast = await toastController.create({
        message,
        duration: 1800,
        position: 'top',
        color,
      });
      await toast.present();
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  --background: transparent;
  box-shadow: none;
}

.admin-card--glass {
  border-radius: 18px;
  border: 1px solid var(--liga-float-glass-border, rgba(255, 255, 255, 0.14));
  background: var(--liga-float-glass-bg-strong, rgba(28, 28, 32, 0.72));
  backdrop-filter: var(--liga-float-glass-blur, blur(18px));
  -webkit-backdrop-filter: var(--liga-float-glass-blur, blur(18px));
  box-shadow: var(--liga-float-glass-shadow, 0 8px 28px rgba(0, 0, 0, 0.28));
  overflow: hidden;
}

.admin-card ion-card-title {
  color: var(--ion-text-color);
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.admin-card-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.admin-card ion-card-content {
  color: var(--ion-text-color);
}

.admin-card-subtitle,
.admin-hint {
  margin: 0 0 18px;
  color: var(--liga-chrome-text-muted);
  line-height: 1.45;
  font-size: 0.9rem;
}

.update-status-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 11px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.update-status-badge--on {
  background: rgba(45, 211, 111, 0.2);
  color: #7dffb0;
  border: 1px solid rgba(45, 211, 111, 0.35);
}

.update-status-badge--off {
  background: rgba(255, 255, 255, 0.08);
  color: var(--liga-chrome-text-muted);
  border: 1px solid var(--liga-float-glass-border);
}

.update-prompt-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.update-prompt-control-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--liga-float-glass-border);
}

.update-prompt-toggle {
  flex: 1 1 auto;
  min-width: 220px;
  justify-content: space-between;
}

.update-prompt-ion-toggle {
  --background-checked: rgba(232, 93, 4, 0.95);
  --handle-background-checked: #fff;
}

.update-campaign-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 0.72rem;
  font-weight: 700;
  color: rgba(255, 220, 190, 0.95);
  background: rgba(232, 93, 4, 0.18);
  border: 1px solid rgba(232, 93, 4, 0.35);
  white-space: nowrap;
}

.update-prompt-message-wrap {
  position: relative;
  border-radius: 14px;
  padding: 12px 14px 28px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid var(--liga-float-glass-border);
}

.update-prompt-message-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--liga-chrome-text-muted);
  margin-bottom: 6px;
}

.update-prompt-textarea {
  --background: transparent;
  --padding-start: 0;
  --padding-end: 0;
  --padding-top: 0;
  --padding-bottom: 0;
  --color: var(--liga-chrome-text);
  --placeholder-color: var(--liga-chrome-text-muted);
  font-size: 0.95rem;
  line-height: 1.4;
  width: 100%;
}

.update-prompt-message-hint {
  position: absolute;
  right: 12px;
  bottom: 8px;
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.4);
}

.update-prompt-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.update-prompt-btn {
  --border-radius: 999px;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: none;
  margin: 0;
}

.update-prompt-btn--save {
  --background: rgba(232, 93, 4, 0.92);
  --background-activated: rgba(232, 93, 4, 1);
  --background-hover: rgba(232, 93, 4, 1);
  --color: var(--liga-chrome-text);
  --box-shadow: none;
}

.update-prompt-btn--reshow {
  --border-width: 1px;
  --border-color: rgba(140, 200, 255, 0.45);
  --color: rgba(210, 235, 255, 0.95);
  --background: rgba(120, 190, 255, 0.08);
  --background-activated: rgba(120, 190, 255, 0.16);
  --background-hover: rgba(120, 190, 255, 0.14);
}

.update-prompt-btn[disabled] {
  opacity: 0.45;
}

.app-sections-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.app-section-row {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(520px, 58%);
  gap: 16px;
  align-items: center;
  padding: 14px;
  border: 1px solid var(--liga-float-glass-border);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
}

.app-section-title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.app-section-info h3 {
  margin: 0;
  color: var(--ion-text-color);
  font-size: 1rem;
  font-weight: 700;
}

.app-section-key,
.app-section-role {
  margin: 4px 0 0;
  color: var(--ion-text-color);
  opacity: 0.72;
  font-size: 0.82rem;
  word-break: break-word;
}

.app-section-status {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
}

.app-section-status--public {
  background: var(--ion-color-success);
  color: var(--ion-color-success-contrast);
}

.app-section-status--role {
  background: var(--ion-color-warning);
  color: var(--ion-color-warning-contrast);
}

.app-section-controls {
  display: grid;
  grid-template-columns: auto minmax(320px, 1fr);
  gap: 10px;
  align-items: center;
}

.app-section-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  color: var(--ion-text-color);
  font-size: 0.8rem;
  white-space: nowrap;
}

.app-section-role-select {
  --background: rgba(255, 255, 255, 0.06);
  --background-activated: rgba(255, 255, 255, 0.08);
  --background-hover: rgba(255, 255, 255, 0.08);
  --border-radius: 10px;
  --color: var(--ion-text-color);
  --min-height: 52px;
  border-radius: 10px;
  min-width: 260px;
  width: 100%;
}

.app-section-role-select--disabled {
  opacity: 0.5;
}

@media (max-width: 900px) {
  .app-section-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .app-section-controls {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .app-section-toggle {
    justify-content: space-between;
    width: 100%;
    padding: 2px 0;
  }

  .app-section-role-select {
    min-width: 0;
  }

}

@media (max-width: 480px) {
  .admin-card {
    margin: 12px 10px;
  }

  .app-section-row {
    padding: 12px;
    border-radius: 12px;
  }

  .app-section-info h3 {
    font-size: 0.95rem;
  }

  .app-section-status {
    font-size: 0.68rem;
  }
}

:global(.admin-role-select-popover) {
  --width: min(460px, calc(100vw - 32px));
  --max-height: min(520px, calc(100vh - 96px));
}

:global(.admin-role-select-popover::part(content)) {
  width: min(460px, calc(100vw - 32px));
  max-height: min(520px, calc(100vh - 96px));
}

:global(.admin-role-select-popover ion-content) {
  --padding-top: 4px;
  --padding-bottom: 4px;
}

:global(.admin-role-select-popover ion-item) {
  --min-height: 50px;
}

:global(.admin-role-select-popover ion-label),
:global(.admin-role-select-popover ion-radio::part(label)) {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  line-height: 1.25;
}

/* Светлая тема: тёмные RGBA/hex на стекле ломают контраст */
:global(html.liga-theme-light) .update-status-badge--on {
  background: rgba(45, 160, 90, 0.14);
  color: #1a7a45;
  border-color: rgba(45, 160, 90, 0.35);
}

:global(html.liga-theme-light) .update-status-badge--off {
  background: rgba(30, 30, 40, 0.06);
}

:global(html.liga-theme-light) .update-prompt-control-row,
:global(html.liga-theme-light) .app-section-row {
  background: rgba(30, 30, 40, 0.04);
}

:global(html.liga-theme-light) .update-campaign-chip {
  color: #b34a00;
  background: rgba(232, 93, 4, 0.12);
  border-color: rgba(232, 93, 4, 0.35);
}

:global(html.liga-theme-light) .update-prompt-message-wrap {
  background: rgba(30, 30, 40, 0.05);
}

:global(html.liga-theme-light) .update-prompt-message-hint {
  color: var(--liga-chrome-text-muted);
}

:global(html.liga-theme-light) .update-prompt-btn--reshow {
  --border-color: rgba(40, 110, 180, 0.45);
  --color: #1a5a9a;
  --background: rgba(40, 110, 180, 0.08);
  --background-activated: rgba(40, 110, 180, 0.14);
  --background-hover: rgba(40, 110, 180, 0.12);
}

:global(html.liga-theme-light) .update-prompt-btn--save {
  --color: #ffffff;
}

:global(html.liga-theme-light) .app-section-role-select {
  --background: rgba(30, 30, 40, 0.05);
  --background-activated: rgba(30, 30, 40, 0.08);
  --background-hover: rgba(30, 30, 40, 0.08);
}
</style>
